import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { HiOutlineUser, HiOutlineBuildingOffice, HiOutlineClipboardDocumentList, HiOutlineCheckCircle, HiOutlineClock, HiOutlineXCircle, HiOutlineInformationCircle, HiOutlineCheck, HiOutlineXMark, HiOutlineCreditCard } from 'react-icons/hi2';
import { useAuth } from '../../contexts/AuthContext';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../config/firebaseConfig';
import axios from 'axios';
import PhoneField from '../../components/CustomPhoneInput/CustomPhoneInput';
import './ProfilePage.css';

const ProfilePage = () => {
  const navigate = useNavigate();
  const { 
    currentUser, 
    userData, 
    isApproved, 
    isPending, 
    isRejected, 
    hasApplied,
    affiliateTier,
    applyAsAffiliate,
    refreshUserData 
  } = useAuth();
  
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  const [personalInfo, setPersonalInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    websiteUrl: '',
    selectedSocialPlatforms: [],
    socialMediaLinks: {
      instagram: '',
      x: '',
      tiktok: '',
      youtube: '',
      other: ''
    }
  });
  
  const [businessInfo, setBusinessInfo] = useState({
    businessName: '',
    website: '',
    socialMedia: '',
    businessType: ''
  });
  
  const [applicationInfo, setApplicationInfo] = useState({
    affiliateType: '',
    audienceSize: '',
    marketingChannels: [],
    referralSource: '',
    websiteUrl: '',
    airbnbUrl: '',
    hotelUrl: '',
    selectedSocialPlatforms: [],
    socialMediaLinks: {
      instagram: '',
      x: '',
      tiktok: '',
      youtube: '',
      other: ''
    },
    agreeTerms: false
  });

  const [paymentInfo, setPaymentInfo] = useState({
    billingType: '',
    iban: '',
    fullName: ''
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (userData) {
      setPersonalInfo({
        firstName: userData.firstName || userData.affiliateApplication?.firstName || '',
        lastName: userData.lastName || userData.affiliateApplication?.lastName || '',
        email: userData.email || userData.affiliateApplication?.email || '',
        phone: userData.phone || userData.affiliateApplication?.phone || '',
        websiteUrl: userData.websiteUrl || userData.affiliateApplication?.websiteUrl || '',
        selectedSocialPlatforms: userData.selectedSocialPlatforms || userData.affiliateApplication?.selectedSocialPlatforms || [],
        socialMediaLinks: {
          instagram: userData.socialMediaLinks?.instagram || userData.affiliateApplication?.socialMediaLinks?.instagram || '',
          x: userData.socialMediaLinks?.x || userData.affiliateApplication?.socialMediaLinks?.x || '',
          tiktok: userData.socialMediaLinks?.tiktok || userData.affiliateApplication?.socialMediaLinks?.tiktok || '',
          youtube: userData.socialMediaLinks?.youtube || userData.affiliateApplication?.socialMediaLinks?.youtube || '',
          other: userData.socialMediaLinks?.other || userData.affiliateApplication?.socialMediaLinks?.other || ''
        }
      });
      
      if (userData.affiliateApplication) {
        setBusinessInfo({
          businessName: userData.affiliateApplication.businessName || '',
          website: userData.affiliateApplication.website || '',
          socialMedia: userData.affiliateApplication.socialMedia || '',
          businessType: userData.affiliateApplication.businessType || ''
        });
        
        setApplicationInfo({
          affiliateType: userData.affiliateApplication.affiliateType || '',
          audienceSize: userData.affiliateApplication.audienceSize || '',
          marketingChannels: userData.affiliateApplication.marketingChannels || [],
          referralSource: userData.affiliateApplication.referralSource || '',
          websiteUrl: userData.affiliateApplication.websiteUrl || '',
          airbnbUrl: userData.affiliateApplication.airbnbUrl || '',
          hotelUrl: userData.affiliateApplication.hotelUrl || '',
          selectedSocialPlatforms: userData.affiliateApplication.selectedSocialPlatforms || [],
          socialMediaLinks: {
            instagram: userData.affiliateApplication.socialMediaLinks?.instagram || '',
            x: userData.affiliateApplication.socialMediaLinks?.x || '',
            tiktok: userData.affiliateApplication.socialMediaLinks?.tiktok || '',
            youtube: userData.affiliateApplication.socialMediaLinks?.youtube || '',
            other: userData.affiliateApplication.socialMediaLinks?.other || ''
          },
          agreeTerms: true
        });

        setPaymentInfo({
          billingType: userData.affiliateApplication.billingType || userData.billingType || '',
          iban: userData.affiliateApplication.iban || '',
          fullName: userData.affiliateApplication.fullName || ''
        });
      }
    }
  }, [userData]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handlePersonalChange = (e) => {
    const { name, value } = e.target;
    setPersonalInfo(prev => ({ ...prev, [name]: value }));
  };

  const handlePersonalSocialPlatformToggle = (platform) => {
    setPersonalInfo(prev => {
      const isRemoving = prev.selectedSocialPlatforms.includes(platform);
      const updated = {
        ...prev,
        selectedSocialPlatforms: isRemoving
          ? prev.selectedSocialPlatforms.filter(p => p !== platform)
          : [...prev.selectedSocialPlatforms, platform]
      };
      if (isRemoving) {
        updated.socialMediaLinks = { ...prev.socialMediaLinks, [platform]: '' };
      }
      return updated;
    });
  };

  const handlePersonalSocialMediaLinkChange = (platform, value) => {
    setPersonalInfo(prev => ({
      ...prev,
      socialMediaLinks: { ...prev.socialMediaLinks, [platform]: value }
    }));
  };

  const handleBusinessChange = (e) => {
    const { name, value } = e.target;
    setBusinessInfo(prev => ({ ...prev, [name]: value }));
  };

  const handleApplicationChange = (e) => {
    const { name, value, checked } = e.target;
    if (name === 'agreeTerms') {
      setApplicationInfo(prev => ({ ...prev, [name]: checked }));
    } else {
      setApplicationInfo(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleChannelToggle = (channel) => {
    setApplicationInfo(prev => {
      const isRemoving = prev.marketingChannels.includes(channel);
      const updated = {
        ...prev,
        marketingChannels: isRemoving
          ? prev.marketingChannels.filter(c => c !== channel)
          : [...prev.marketingChannels, channel]
      };
      if (isRemoving && channel === 'website') {
        updated.websiteUrl = '';
      }
      if (isRemoving && channel === 'social_media') {
        updated.selectedSocialPlatforms = [];
        updated.socialMediaLinks = { instagram: '', x: '', tiktok: '', youtube: '', other: '' };
      }
      return updated;
    });
  };

  const handleSocialPlatformToggle = (platform) => {
    setApplicationInfo(prev => {
      const isRemoving = prev.selectedSocialPlatforms.includes(platform);
      const updated = {
        ...prev,
        selectedSocialPlatforms: isRemoving
          ? prev.selectedSocialPlatforms.filter(p => p !== platform)
          : [...prev.selectedSocialPlatforms, platform]
      };
      if (isRemoving) {
        updated.socialMediaLinks = { ...prev.socialMediaLinks, [platform]: '' };
      }
      return updated;
    });
  };

  const handleSocialMediaLinkChange = (platform, value) => {
    setApplicationInfo(prev => ({
      ...prev,
      socialMediaLinks: { ...prev.socialMediaLinks, [platform]: value }
    }));
  };

  const handlePaymentChange = (e) => {
    const { name, value } = e.target;
    setPaymentInfo(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveProfile = async () => {
    setLoading(true);
    try {
      await updateDoc(doc(db, 'users', currentUser.uid), {
        firstName: personalInfo.firstName,
        lastName: personalInfo.lastName,
        phone: personalInfo.phone,
        websiteUrl: personalInfo.websiteUrl,
        selectedSocialPlatforms: personalInfo.selectedSocialPlatforms,
        socialMediaLinks: personalInfo.socialMediaLinks,
        updatedAt: new Date()
      });
      
      await refreshUserData();
      setSnackbar({ open: true, message: 'Profile saved successfully!', severity: 'success' });
    } catch (error) {
      console.error('Error saving profile:', error);
      setSnackbar({ open: true, message: 'Error saving profile', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSavePayment = async () => {
    setLoading(true);
    try {
      await updateDoc(doc(db, 'users', currentUser.uid), {
        'affiliateApplication.billingType': paymentInfo.billingType,
        'affiliateApplication.iban': paymentInfo.iban,
        'affiliateApplication.fullName': paymentInfo.fullName,
        billingType: paymentInfo.billingType,
        updatedAt: new Date()
      });
      
      await refreshUserData();
      setSnackbar({ open: true, message: 'Payment details saved successfully!', severity: 'success' });
    } catch (error) {
      console.error('Error saving payment details:', error);
      setSnackbar({ open: true, message: 'Error saving payment details', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitApplication = async () => {
    if (!applicationInfo.agreeTerms) {
      setSnackbar({ open: true, message: 'Please agree to the terms and conditions', severity: 'error' });
      return;
    }
    
    
    const needsAudienceSize = applicationInfo.affiliateType === 'influencer';
    if (!applicationInfo.affiliateType || (needsAudienceSize && !applicationInfo.audienceSize) || applicationInfo.marketingChannels.length === 0) {
      setSnackbar({ open: true, message: 'Please complete all required application fields', severity: 'error' });
      return;
    }

    setLoading(true);
    try {
      const applicationData = {
        ...personalInfo,
        ...businessInfo,
        ...applicationInfo
      };
      
      await applyAsAffiliate(applicationData);
      
      // Send notification email
      try {
        await axios.post('https://bosp-yachts-backend.vercel.app/api/mailchimp/send-email', {
          templateName: 'adm-affiliate-application',
          email: "info@bosphorusyachts.com",
          firstName: "Bosphorus",
          lastName: "Yachts",
          emailVars: [
            { name: 'subject', content: "New Affiliate Application" },
            { name: 'subtitle', content: `${personalInfo.firstName} ${personalInfo.lastName} has applied to become an affiliate` },
            { name: 'contentTitle', content: `New Application from ${personalInfo.firstName} ${personalInfo.lastName}` },
            { name: 'content', content: `Business: ${businessInfo.businessName}\nEmail: ${personalInfo.email}` }
          ]
        });
      } catch (emailError) {
        console.error('Error sending notification email:', emailError);
      }
      
      setSnackbar({ open: true, message: 'Application submitted successfully!', severity: 'success' });
    } catch (error) {
      console.error('Error submitting application:', error);
      setSnackbar({ open: true, message: 'Error submitting application', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const businessTypeOptions = [
    { value: 'travel_agency', label: 'Travel Agency' },
    { value: 'tour_operator', label: 'Tour Operator' },
    { value: 'travel_blogger', label: 'Travel Blogger/Influencer' },
    { value: 'hotel_concierge', label: 'Hotel/Concierge Service' },
    { value: 'event_planner', label: 'Event Planner' },
    { value: 'other', label: 'Other' }
  ];

  const affiliateTypeOptions = [
    { value: 'influencer', label: 'Influencer' },
    { value: 'airbnb_host', label: 'Airbnb Host' },
    { value: 'hotel_host', label: 'Hotel Host' }
  ];

  const audienceSizeOptions = [
    { value: 'less_than_1000', label: 'Less than 1,000' },
    { value: '1000_5000', label: '1,000 - 5,000' },
    { value: '5001_10000', label: '5,001 - 10,000' },
    { value: '10001_50000', label: '10,001 - 50,000' },
    { value: '50001_100000', label: '50,001 - 100,000' },
    { value: 'more_than_100000', label: 'More than 100,000' }
  ];

  const marketingChannels = [
    { value: 'website', label: 'Website/Blog' },
    { value: 'email', label: 'Email Marketing' },
    { value: 'social_media', label: 'Social Media' },
    { value: 'paid_ads', label: 'Paid Advertising' },
    { value: 'direct', label: 'Direct Referrals' }
  ];

  const renderStatusBadge = () => {
    if (isApproved) {
      return (
        <div className="status-badge approved">
          <HiOutlineCheckCircle />
          <span>Approved - Tier {affiliateTier || 1}</span>
        </div>
      );
    } else if (isPending) {
      return (
        <div className="status-badge pending">
          <HiOutlineClock />
          <span>Pending Approval</span>
        </div>
      );
    } else if (isRejected) {
      return (
        <div className="status-badge rejected">
          <HiOutlineXCircle />
          <span>Application Rejected</span>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="profile-page">
      <div className="profile-container">
        <div className="profile-header">
          <h1 className="profile-title">My Profile</h1>
          {renderStatusBadge()}
        </div>

        {isPending && (
          <div className="profile-alert info">
            <HiOutlineInformationCircle />
            <span>Your application is being reviewed. We'll notify you once a decision is made.</span>
          </div>
        )}

        {isRejected && (
          <div className="profile-alert error">
            <HiOutlineXCircle />
            <span>Your application was not approved. Please contact support for more information.</span>
          </div>
        )}

        <div className="profile-card">
          <div className="profile-tabs">
            <button className={`profile-tab ${activeTab === 0 ? 'active' : ''}`} onClick={() => setActiveTab(0)}>
              <HiOutlineUser /><span>Personal Info</span>
            </button>
            {hasApplied && (
              <button className={`profile-tab ${activeTab === 3 ? 'active' : ''}`} onClick={() => setActiveTab(3)}>
                <HiOutlineCreditCard /><span>Payment</span>
              </button>
            )}
            {!hasApplied && (
              <button className={`profile-tab ${activeTab === 2 ? 'active' : ''}`} onClick={() => setActiveTab(2)}>
                <HiOutlineClipboardDocumentList /><span>Apply</span>
              </button>
            )}
          </div>

          <div className="tab-content">
            {/* Personal Info Tab */}
            {activeTab === 0 && (
              <div>
                <h3 className="tab-section-title">Personal Information</h3>
                <div className="form-grid">
                  <div className="form-group">
                    <label>First Name</label>
                    <input
                      type="text"
                      name="firstName"
                      value={personalInfo.firstName}
                      onChange={handlePersonalChange}
                      placeholder="Enter your first name"
                    />
                  </div>
                  <div className="form-group">
                    <label>Last Name</label>
                    <input
                      type="text"
                      name="lastName"
                      value={personalInfo.lastName}
                      onChange={handlePersonalChange}
                      placeholder="Enter your last name"
                    />
                  </div>
                  <div className="form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      name="email"
                      value={personalInfo.email}
                      disabled
                    />
                  </div>
                  <div className="form-group">
                    <label>Phone</label>
                    <div className="profile-phone-wrapper">
                      <PhoneField
                        label=""
                        value={personalInfo.phone}
                        onChange={(val) => setPersonalInfo(prev => ({ ...prev, phone: val }))}
                      />
                    </div>
                  </div>
                  <div className="form-group full-width">
                    <label>Website URL</label>
                    <input
                      type="url"
                      name="websiteUrl"
                      value={personalInfo.websiteUrl}
                      onChange={handlePersonalChange}
                      placeholder="https://yourwebsite.com"
                    />
                  </div>
                  <div className="form-group full-width social-links-section">
                    <label>Social Media Links</label>
                    <div className="social-platform-toggles">
                      {[
                        { key: 'instagram', label: 'Instagram', placeholder: 'https://instagram.com/yourprofile' },
                        { key: 'x', label: 'X (Twitter)', placeholder: 'https://x.com/yourprofile' },
                        { key: 'tiktok', label: 'TikTok', placeholder: 'https://tiktok.com/@yourprofile' },
                        { key: 'youtube', label: 'YouTube', placeholder: 'https://youtube.com/@yourchannel' },
                        { key: 'other', label: 'Other', placeholder: 'https://...' }
                      ].map(platform => (
                        <div key={platform.key} className="social-platform-item">
                          <div
                            className={`checkbox-item ${personalInfo.selectedSocialPlatforms.includes(platform.key) ? 'checked' : ''}`}
                            onClick={() => handlePersonalSocialPlatformToggle(platform.key)}
                          >
                            <div className="checkbox-box"><HiOutlineCheck /></div>
                            <span className="checkbox-label">{platform.label}</span>
                          </div>
                          {personalInfo.selectedSocialPlatforms.includes(platform.key) && (
                            <input
                              type="url"
                              className="social-platform-link-input"
                              value={personalInfo.socialMediaLinks[platform.key]}
                              onChange={(e) => handlePersonalSocialMediaLinkChange(platform.key, e.target.value)}
                              placeholder={platform.placeholder}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="form-actions">
                  <button className="btn-primary" onClick={handleSaveProfile} disabled={loading}>
                    {loading ? <span className="btn-spinner"></span> : 'Save Changes'}
                  </button>
                </div>
              </div>
            )}

            {/* Application Tab */}
            {activeTab === 2 && !hasApplied && (
              <div>
                <h3 className="tab-section-title">Affiliate Application</h3>
                <p className="tab-section-subtitle">Complete this form to apply for our affiliate program.</p>
                
                <div className="form-grid">
                  <div className="form-group">
                    <label>I am a *</label>
                    <select
                      name="affiliateType"
                      value={applicationInfo.affiliateType}
                      onChange={handleApplicationChange}
                    >
                      <option value="">Select your type</option>
                      {affiliateTypeOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                  {applicationInfo.affiliateType === 'airbnb_host' && (
                    <div className="form-group">
                      <label>Airbnb Listing URL</label>
                      <input
                        type="url"
                        name="airbnbUrl"
                        value={applicationInfo.airbnbUrl}
                        onChange={handleApplicationChange}
                        placeholder="https://airbnb.com/rooms/your-listing"
                      />
                    </div>
                  )}
                  {applicationInfo.affiliateType === 'hotel_host' && (
                    <div className="form-group">
                      <label>Hotel Website URL</label>
                      <input
                        type="url"
                        name="hotelUrl"
                        value={applicationInfo.hotelUrl}
                        onChange={handleApplicationChange}
                        placeholder="https://your-hotel-website.com"
                      />
                    </div>
                  )}
                  {applicationInfo.affiliateType === 'influencer' && (
                    <div className="form-group">
                      <label>Audience Size *</label>
                      <select
                        name="audienceSize"
                        value={applicationInfo.audienceSize}
                        onChange={handleApplicationChange}
                      >
                        <option value="">Select audience size</option>
                        {audienceSizeOptions.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div className="form-group full-width">
                    <label className="checkbox-group-label">Marketing Channels *</label>
                    <div className="checkbox-group">
                      {marketingChannels.map(channel => (
                        <div 
                          key={channel.value} 
                          className={`checkbox-item ${applicationInfo.marketingChannels.includes(channel.value) ? 'checked' : ''}`}
                          onClick={() => handleChannelToggle(channel.value)}
                        >
                          <div className="checkbox-box"><HiOutlineCheck /></div>
                          <span className="checkbox-label">{channel.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {applicationInfo.marketingChannels.includes('website') && (
                    <div className="form-group full-width">
                      <label>Website URL *</label>
                      <input
                        type="url"
                        name="websiteUrl"
                        value={applicationInfo.websiteUrl}
                        onChange={handleApplicationChange}
                        placeholder="https://yourwebsite.com"
                      />
                    </div>
                  )}

                  {applicationInfo.marketingChannels.includes('social_media') && (
                    <div className="form-group full-width social-links-section">
                      <label>Select your social media platforms</label>
                      <div className="social-platform-toggles">
                        {[
                          { key: 'instagram', label: 'Instagram', placeholder: 'https://instagram.com/yourprofile' },
                          { key: 'x', label: 'X (Twitter)', placeholder: 'https://x.com/yourprofile' },
                          { key: 'tiktok', label: 'TikTok', placeholder: 'https://tiktok.com/@yourprofile' },
                          { key: 'youtube', label: 'YouTube', placeholder: 'https://youtube.com/@yourchannel' },
                          { key: 'other', label: 'Other', placeholder: 'https://...' }
                        ].map(platform => (
                          <div key={platform.key} className="social-platform-item">
                            <div
                              className={`checkbox-item ${applicationInfo.selectedSocialPlatforms.includes(platform.key) ? 'checked' : ''}`}
                              onClick={() => handleSocialPlatformToggle(platform.key)}
                            >
                              <div className="checkbox-box"><HiOutlineCheck /></div>
                              <span className="checkbox-label">{platform.label}</span>
                            </div>
                            {applicationInfo.selectedSocialPlatforms.includes(platform.key) && (
                              <input
                                type="url"
                                className="social-platform-link-input"
                                value={applicationInfo.socialMediaLinks[platform.key]}
                                onChange={(e) => handleSocialMediaLinkChange(platform.key, e.target.value)}
                                placeholder={platform.placeholder}
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="form-group full-width">
                    <div 
                      className={`terms-checkbox ${applicationInfo.agreeTerms ? 'checked' : ''}`}
                      onClick={() => setApplicationInfo(prev => ({ ...prev, agreeTerms: !prev.agreeTerms }))}
                    >
                      <div className="checkbox-box"><HiOutlineCheck /></div>
                      <span>I agree to the Terms and Conditions and Privacy Policy *</span>
                    </div>
                  </div>
                </div>

                <div className="form-actions">
                  <button className="btn-primary" onClick={handleSubmitApplication} disabled={loading}>
                    {loading ? <span className="btn-spinner"></span> : 'Submit Application'}
                  </button>
                </div>
              </div>
            )}

            {/* Payment Tab */}
            {activeTab === 3 && hasApplied && (
              <div>
                <h3 className="tab-section-title">Payment Settings</h3>
                <p className="tab-section-subtitle">Update your payment information to receive affiliate commissions.</p>
                
                <div className="form-grid full-width">
                  <div className="form-group full-width">
                    <label>Billing Status *</label>
                    <select
                      name="billingType"
                      value={paymentInfo.billingType}
                      onChange={handlePaymentChange}
                    >
                      <option value="">Select your billing status...</option>
                      <option value="turkish_company">I have a Turkish company and Turkish VAT number</option>
                      <option value="turkish_citizen">I'm a Turkish citizen but do not have a company or VAT number</option>
                      <option value="foreign_company">I have a company outside Turkey</option>
                    </select>
                  </div>
                </div>

                {paymentInfo.billingType && (
                  <div className="billing-info-note">
                    <HiOutlineInformationCircle />
                    <span>
                      {paymentInfo.billingType === 'turkish_company' && 'You can receive 100% of the balance (VAT included).'}
                      {paymentInfo.billingType === 'turkish_citizen' && 'You can receive 70% of the earnings.'}
                      {paymentInfo.billingType === 'foreign_company' && 'You can receive 65% of the total amount.'}
                    </span>
                  </div>
                )}

                <h3 className="tab-section-title" style={{ marginTop: '2rem' }}>Bank Details</h3>
                <div className="form-grid full-width">
                  <div className="form-group full-width">
                    <label>IBAN</label>
                    <input
                      type="text"
                      name="iban"
                      value={paymentInfo.iban}
                      onChange={handlePaymentChange}
                      placeholder="Enter your IBAN (e.g., TR00 0000 0000 0000 0000 0000 00)"
                    />
                  </div>
                  <div className="form-group full-width">
                    <label>Full Name (Account Holder)</label>
                    <input
                      type="text"
                      name="fullName"
                      value={paymentInfo.fullName}
                      onChange={handlePaymentChange}
                      placeholder="Enter the full name on the bank account"
                    />
                  </div>
                </div>

                <div className="payment-note">
                  <HiOutlineInformationCircle />
                  <span>Your commission payments will be transferred to this bank account. Please ensure the IBAN and account holder name are correct to avoid payment delays.</span>
                </div>

                <div className="form-actions">
                  <button className="btn-primary" onClick={handleSavePayment} disabled={loading}>
                    {loading ? <span className="btn-spinner"></span> : 'Save Payment Details'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {snackbar.open && (
        <div className="toast-container">
          <div className={`toast ${snackbar.severity}`}>
            {snackbar.severity === 'success' ? <HiOutlineCheckCircle /> : <HiOutlineXCircle />}
            <span>{snackbar.message}</span>
            <button className="toast-close" onClick={() => setSnackbar({ ...snackbar, open: false })}>
              <HiOutlineXMark />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfilePage;
